import React, { useCallback, useEffect, useState } from 'react';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import {
  DownloadFormat,
  Fonts,
  TextAlignment,
  DocumentDownloadOptions,
} from '../../../../../../backend/src/types/types';
import { FONT_FAMILY_OPTIONS, TEXT_ALIGNMENT_OPTIONS } from '@/constants/downloadOptions';
import { cn } from '@/helpers/shadCnUtils';
import { usePermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { Textarea } from '@/new/components/shadcn/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface DownloadWrittenModalProps {
  downloadTitle: string;
  loadingDownload: boolean;
  showDocumentOptions?: boolean;
  showStyle?: boolean;
  showSourceDocumentsOption?: boolean;
  handleDownload: ({
    title,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments,
  }: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
    includeSourceDocuments?: boolean;
  }) => void;
  showEmailNotification?: boolean;
}

export const DownloadWrittenModal = ({
  downloadTitle,
  loadingDownload,
  handleDownload,
  showStyle = true,
  showDocumentOptions = false,
  showSourceDocumentsOption = false,
  showEmailNotification = false,
}: DownloadWrittenModalProps) => {
  const { allowed: canDownloadDocs } = usePermission(Permission.DocumentDownload);
  const [title, setTitle] = useState(downloadTitle);
  const [style, setStyle] = useState('');
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);
  const [documentOptions, setDocumentOptions] = useState<DocumentDownloadOptions>({
    fontFamily: Fonts.TimesNewRoman,
    textAlignment: TextAlignment.Left,
    includeReferences: true,
  });

  useEffect(() => {
    setTitle(downloadTitle);
  }, [downloadTitle]);

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleChangeStyle = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setStyle(e.target.value);
  }, []);

  const handleDownloadClick = () => {
    handleDownload({
      title,
      style,
      downloadFormat: DownloadFormat.DOCX,
      documentOptions,
      includeSourceDocuments,
    });
  };

  return (
    <>
      {loadingDownload ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl font-medium text-gray-700">Preparing your download...</div>
          <Icon className="lucide--loader-circle animate-spin text-4xl text-teal-500" />
        </div>
      ) : (
        <div>
          <div className="mb-4 flex flex-col gap-2">
            <Label htmlFor="title" className="text-sm font-medium text-gray-700">
              File Name
            </Label>
            <Input
              id="title"
              onChange={handleChangeTitle}
              value={title}
              placeholder="Enter a title for your file..."
              className="w-full"
            />
          </div>
          {showStyle && (
            <div className="mb-6 flex flex-col gap-2">
              <Label htmlFor="style" className="text-sm font-medium text-gray-700">
                Document Style (optional)
              </Label>
              <Textarea
                id="style"
                onChange={handleChangeStyle}
                value={style}
                placeholder="Write instructions or paste examples of how you would like each fact to be formatted. For example: a particular date format, to use more direct quotes from source text..."
                className="h-32 max-h-52 w-full"
              />
              <p className="text-xs text-gray-500">
                Wexler rewrites each fact according to the style or format you've instructed above. Visual style
                instructions will not be reflected in the download at this time.
              </p>
            </div>
          )}

          {showDocumentOptions && (
            <>
              <div className="mb-4 space-y-2">
                <Label className="text-sm font-medium">Font</Label>
                <Select
                  value={documentOptions?.fontFamily}
                  onValueChange={(value: Fonts) => setDocumentOptions({ ...documentOptions, fontFamily: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select font" />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_FAMILY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-4 space-y-2">
                <Label className="text-sm font-medium">Text Alignment</Label>
                <Select
                  value={documentOptions?.textAlignment}
                  onValueChange={(value: TextAlignment) =>
                    setDocumentOptions({ ...documentOptions, textAlignment: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select alignment" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEXT_ALIGNMENT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6 flex items-center justify-between">
                <div className="flex flex-col">
                  <Label className="text-sm font-medium">Include References</Label>
                  <span className="mt-1 text-xs text-gray-500">
                    {documentOptions?.includeReferences
                      ? 'References section will be added at the end'
                      : 'No references will be included'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Label
                    className={cn(
                      'm-0 px-2 text-sm text-gray-700 transition-colors duration-150',
                      !documentOptions?.includeReferences && 'text-teal-500',
                    )}
                  >
                    No
                  </Label>
                  <Switch
                    className="bg-gray-100"
                    checked={documentOptions?.includeReferences}
                    onCheckedChange={(checked) =>
                      setDocumentOptions({ ...documentOptions, includeReferences: checked })
                    }
                  />
                  <Label
                    className={cn(
                      'm-0 text-sm text-gray-700 transition-colors duration-150',
                      documentOptions?.includeReferences && 'text-teal-500',
                    )}
                  >
                    Yes
                  </Label>
                </div>
              </div>
            </>
          )}

          {showSourceDocumentsOption && (
            <div className="mb-4 flex flex-col gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={cn('flex items-center gap-3', !canDownloadDocs && 'opacity-50')}>
                    <Checkbox
                      id="includeSourceDocuments"
                      checked={canDownloadDocs ? includeSourceDocuments : false}
                      disabled={!canDownloadDocs}
                      onCheckedChange={(checked) => {
                        setIncludeSourceDocuments(Boolean(checked));
                      }}
                    />
                    <Label
                      htmlFor="includeSourceDocuments"
                      className={cn('cursor-pointer text-sm text-gray-700', !canDownloadDocs && 'cursor-not-allowed')}
                    >
                      <div className="font-medium">Include source documents</div>
                      <div className="text-xs text-gray-500">
                        Download a zip containing the chronology and all referenced documents
                      </div>
                    </Label>
                  </div>
                </TooltipTrigger>
                {!canDownloadDocs && (
                  <TooltipContent>
                    <p>You do not have permission to download source documents</p>
                  </TooltipContent>
                )}
              </Tooltip>
              {showEmailNotification && includeSourceDocuments && (
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <Icon className="lucide--mail size-3.5 text-gray-500" />
                  You will receive an email when the export is ready.
                </div>
              )}
            </div>
          )}

          <div className="my-6 border-t border-gray-200"></div>

          <Button onClick={handleDownloadClick} variant="primary" className="w-full">
            Download
            <Icon className="lucide--download" />
          </Button>
        </div>
      )}
    </>
  );
};
